import { DateTime } from 'luxon';
import { StreamQualityReportResponseDto } from '../stream-quality.types';

/**
 * One timeline section bucket: stations sharing the same UTC offset at report start and end.
 * Different IANA zones that resolve to the same offset pair are merged.
 */
export type UtcOffsetPairGroup = {
  /** Stable key, e.g. "-300:-240". */
  id: string;
  startOffsetMinutes: number;
  endOffsetMinutes: number;
  /** Single offset label or "UTC-03 → UTC-02" when offsets differ across the window. */
  label: string;
  /** Distinct IANA timezone ids assigned to stations in this bucket. */
  timeZones: string[];
  stationIds: string[];
};

export type GroupStationsByUtcOffsetPairParams = {
  fromIso: string;
  toIso: string;
  stationIds: readonly string[];
  getTimeZoneForStation: (stationId: string) => string | null | undefined;
  /** Used when a station has no timezone or an invalid IANA id. */
  fallbackTimeZone: string;
};

/**
 * Formats offset minutes as `UTC+2` / `UTC-5:30` (no leading zero on whole hours when minutes are zero).
 */
export function formatUtcOffsetMinutesLabel(offsetMinutes: number): string {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  if (minutes === 0) {
    return `UTC${sign}${hours}`;
  }
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  return `UTC${sign}${hh}:${mm}`;
}

/**
 * Returns UTC offset in minutes for an instant in the given IANA zone, or null if the zone is invalid.
 */
export function getUtcOffsetMinutesAt(instant: Date, ianaZone: string): number | null {
  const dt = DateTime.fromMillis(instant.getTime(), { zone: 'utc' }).setZone(ianaZone.trim());
  if (!dt.isValid) {
    return null;
  }
  return dt.offset;
}

function formatOffsetPairLabel(startOffsetMinutes: number, endOffsetMinutes: number): string {
  const startLabel = formatUtcOffsetMinutesLabel(startOffsetMinutes);
  if (startOffsetMinutes === endOffsetMinutes) {
    return startLabel;
  }
  return `${startLabel} → ${formatUtcOffsetMinutesLabel(endOffsetMinutes)}`;
}

function resolveIanaZone(
  raw: string | null | undefined,
  fallbackTimeZone: string,
): { zone: string; usedFallback: boolean } {
  const trimmed = raw?.trim();
  if (!trimmed) {
    return { zone: fallbackTimeZone, usedFallback: true };
  }
  const probe = DateTime.fromMillis(Date.now(), { zone: 'utc' }).setZone(trimmed);
  if (!probe.isValid) {
    return { zone: fallbackTimeZone, usedFallback: true };
  }
  return { zone: trimmed, usedFallback: false };
}

function offsetPairKey(startOffsetMinutes: number, endOffsetMinutes: number): string {
  return `${startOffsetMinutes}:${endOffsetMinutes}`;
}

/**
 * Buckets filtered stations by `(offsetAtReportStart, offsetAtReportEnd)` in each station's IANA zone.
 */
export function groupStationsByUtcOffsetPair(
  params: GroupStationsByUtcOffsetPairParams,
): UtcOffsetPairGroup[] {
  const { fromIso, toIso, stationIds, getTimeZoneForStation, fallbackTimeZone } = params;
  const fromDate = new Date(fromIso);
  const toDate = new Date(toIso);
  if (
    Number.isNaN(fromDate.getTime()) ||
    Number.isNaN(toDate.getTime()) ||
    !(toDate.getTime() > fromDate.getTime())
  ) {
    return [];
  }

  type Bucket = {
    startOffsetMinutes: number;
    endOffsetMinutes: number;
    timeZones: Set<string>;
    stationIds: string[];
  };

  const buckets = new Map<string, Bucket>();

  for (const stationId of stationIds) {
    const { zone, usedFallback } = resolveIanaZone(
      getTimeZoneForStation(stationId),
      fallbackTimeZone,
    );
    const startOff = getUtcOffsetMinutesAt(fromDate, zone);
    const endOff = getUtcOffsetMinutesAt(toDate, zone);
    if (startOff == null || endOff == null) {
      continue;
    }
    const id = offsetPairKey(startOff, endOff);
    let bucket = buckets.get(id);
    if (!bucket) {
      bucket = {
        startOffsetMinutes: startOff,
        endOffsetMinutes: endOff,
        timeZones: new Set(),
        stationIds: [],
      };
      buckets.set(id, bucket);
    }
    if (!usedFallback) {
      bucket.timeZones.add(zone);
    }
    bucket.stationIds.push(stationId);
  }

  const groups: UtcOffsetPairGroup[] = [...buckets.entries()].map(([id, b]) => ({
    id,
    startOffsetMinutes: b.startOffsetMinutes,
    endOffsetMinutes: b.endOffsetMinutes,
    label: formatOffsetPairLabel(b.startOffsetMinutes, b.endOffsetMinutes),
    timeZones: [...b.timeZones].sort((a, c) => a.localeCompare(c)),
    stationIds: b.stationIds,
  }));

  groups.sort((a, b) => {
    if (a.startOffsetMinutes !== b.startOffsetMinutes) {
      return a.startOffsetMinutes - b.startOffsetMinutes;
    }
    if (a.endOffsetMinutes !== b.endOffsetMinutes) {
      return a.endOffsetMinutes - b.endOffsetMinutes;
    }
    return a.id.localeCompare(b.id);
  });

  return groups;
}

/**
 * Convenience overload using `StreamQualityReportResponseDto` window and a station timezone resolver.
 */
export function groupStationsByUtcOffsetPairForReport(
  report: StreamQualityReportResponseDto,
  stationIds: readonly string[],
  getTimeZoneForStation: (stationId: string) => string | null | undefined,
  fallbackTimeZone: string,
): UtcOffsetPairGroup[] {
  return groupStationsByUtcOffsetPair({
    fromIso: report.data.from,
    toIso: report.data.to,
    stationIds,
    getTimeZoneForStation,
    fallbackTimeZone,
  });
}
